import React, { useState } from "react";
import { getMuteState, toggleMute as toggleSoundMute } from "./utils/soundManager";
import { getGreeting } from "./services/gameLogic";

// Hooks
import { useAuth } from "./hooks/useAuth";
import { useGameState } from "./hooks/useGameState";

// Screens
import ProfileScreen from "./screens/ProfileScreen";
import InputScreen from "./screens/InputScreen";
import PreviewScreen from "./screens/PreviewScreen";
import GameScreen from "./screens/GameScreen";
import ResultScreen from "./screens/ResultScreen";
import SupportSuccessScreen from "./screens/SupportSuccessScreen";

// Components
import HomeScreen from "./components/HomeScreen";
import StreakStatusModal from "./components/StreakStatusModal";
import OnboardingContainer from "./components/onboarding/OnboardingContainer";
import { PaymentSuccessScreen } from "./components/onboarding/screens/PaymentSuccessScreen";
import { OnboardingProvider } from "./context/OnboardingContext";
import PwaInstallModal from "./components/pwa/PwaInstallModal";

function App() {
  // ================= THEME =================
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem("app_theme");
    return saved || "light";
  });

  // ================= ROUTING =================
  const [step, setStep] = useState("loading");

  const [muted, setMuted] = useState(getMuteState());

  // ================= HOOKS =================
  const auth = useAuth({ step, setStep });
  const game = useGameState({ user: auth.user, setUser: auth.setUser, step, setStep, muted, setMuted });

  // Handle /payment-success route
  React.useEffect(() => {
    if (window.location.pathname === "/payment-success") {
      setStep("payment-success");
    } else if (window.location.pathname === "/support-success") {
      setStep("support-success");
    }
  }, [setStep]);

  // FIX: Surgical scroll reset for persistent #root container
  // Since index.css defines #root as the scroll container (overflow-y: auto),
  // we must reset its scrollTop synchronously during screen transitions.
  React.useLayoutEffect(() => {
    const root = document.getElementById("root");
    if (root) {
      root.scrollTop = 0;
    }
  }, [step]);

  // ================= STYLES =================
  const isDark = theme === "dark";
  const colors = {
    background: isDark ? "#0F172A" : "#F8FBFA",
    surface: isDark ? "#1E293B" : "#FFFFFF",
    text: isDark ? "#F1F5F9" : "#1E293B",
    secondaryText: isDark ? "#94A3B8" : "#64748B",
    border: isDark ? "rgba(16, 185, 129, 0.12)" : "rgba(16, 185, 129, 0.08)"
  };

  const container = {
    width: "100%", maxWidth: "100vw", minHeight: "100dvh",
    background: colors.background,
    color: colors.text, display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "flex-start", padding: 0, overflow: "auto", position: "relative",
  };



  // ================= ROUTING LOGIC =================
  const renderStep = () => {
    if (step === "loading") {
      return (
        <div style={{ ...container, justifyContent: "center", background: colors.background }}>
          <div style={{ width: "32px", height: "32px", border: "4px solid #1AA39A", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      );
    }



    if (step === "onboarding") {
      return <OnboardingContainer theme={theme} user={auth.user} handleLogout={auth.handleLogout} onComplete={() => {
        localStorage.setItem("onboarding_complete", "true");
        setStep("loading");
      }} />;
    }

    if (step === "payment-success") {
      return <PaymentSuccessScreen refreshUser={auth.refreshUser} user={auth.user} setStep={setStep} />;
    }

    if (step === "support-success") {
      return <SupportSuccessScreen theme={theme} onContinue={() => {
        window.history.replaceState({}, "", "/");
        setStep("home");
      }} />;
    }

    if (step === "home") {
      return (
        <>
          <HomeScreen
            theme={theme}
            setTheme={setTheme}
            user={auth.user} getGreeting={getGreeting} setStep={setStep}
            setGameType={game.setGameType} handleOpenProfile={auth.handleOpenProfile}
            muted={muted}
            toggleSoundMute={() => setMuted(toggleSoundMute())}
          />
          <StreakStatusModal
            theme={theme}
            status={auth.startupStreakStatus}
            setStatus={auth.setStartupStreakStatus}
            user={auth.user}
            onRestore={auth.handleRestoreStreak}
            canRestore={auth.canRestoreStreak}
          />
          <PwaInstallModal />
        </>
      );
    }

    if (step === "input") {
      return <InputScreen
        theme={theme}
        setTheme={setTheme}
        muted={muted}
        toggleSoundMute={() => setMuted(toggleSoundMute())}
        showMeaningFeedback={game.showMeaningFeedback}
        toggleMeaningFeedback={game.toggleMeaningFeedback}
        user={auth.user} goHome={game.goHome} handleOpenProfile={auth.handleOpenProfile}
        handleSetupStart={game.handleSetupStart} selectedSet={game.selectedSet}
        setSelectedSet={game.setSelectedSet} gameType={game.gameType}
        gameLength={game.gameLength} setGameLength={game.setGameLength}
        playMode={game.playMode} setPlayMode={game.setPlayMode}
        isStarting={game.isStarting}
      />;
    }

    if (step === "preview") {
      return <PreviewScreen
        theme={theme}
        goHome={game.goHome} words={game.words} gameType={game.gameType}
        setQuestionStartTime={game.setQuestionStartTime} setStep={setStep}
      />;
    }

    if (step === "game") {
      return <GameScreen
        theme={theme}
        setTheme={setTheme}
        isGameOver={game.isGameOver} gameType={game.gameType} gameLength={game.gameLength}
        feedback={game.feedback} playMode={game.playMode} isPaused={game.isPaused}
        goHome={game.goHome} lives={game.lives} currentIndex={game.currentIndex}
        words={game.words} score={game.score} elapsedTime={game.elapsedTime}
        currentWord={game.currentWord} handleConnotation={game.handleConnotation}
        handleVisualization={game.handleVisualization} handleOptionClick={game.handleOptionClick}
        handleLearnContinue={game.handleLearnContinue} options={game.options}
        selectedOption={game.selectedOption}
        showLivesOverPopup={game.showLivesOverPopup} confirmLivesOver={game.confirmLivesOver}
        isSettingsOpen={game.isSettingsOpen} handleOpenSettings={game.handleOpenSettings}
        handleCloseSettings={game.handleCloseSettings} showMeaningFeedback={game.showMeaningFeedback}
        toggleMeaningFeedback={game.toggleMeaningFeedback} muted={muted} toggleSoundMute={() => setMuted(toggleSoundMute())}
        user={auth.user}
        speedyCount={game.speedyCount}
        isShowingMeaning={game.isShowingMeaning}
        correctOptionId={game.correctOptionId}
        selectedPair={game.selectedPair}
        correctPairIds={game.correctPairIds}
      />;
    }

    if (step === "result") {
      return <ResultScreen
        theme={theme}
        words={game.words} gameType={game.gameType} gameLength={game.gameLength}
        scoreHistoryMap={game.scoreHistoryMap} score={game.score} user={auth.user}
        goHome={game.goHome} currentIndex={game.currentIndex} speedyCount={game.speedyCount}
        setStep={setStep} playMode={game.playMode} lives={game.lives}
        dbHistory={game.dbHistory} isHistoryLoading={game.isHistoryLoading} dbBestScore={game.dbBestScore}
        streakFeedback={game.streakFeedback}
        setStreakFeedback={game.setStreakFeedback}
        workoutReward={game.workoutReward}
      />;
    }

    if (step === "profile") {
      return <ProfileScreen
        theme={theme}
        setTheme={setTheme}
        user={auth.user} setUser={auth.setUser} setStep={setStep} goHome={game.goHome} profileStats={auth.profileStats}
        weakWordsList={auth.weakWordsList}
        masteredWordsList={auth.masteredWordsList} practicedWordsList={auth.practicedWordsList}
        handleLogout={auth.handleLogout} handleSaveUsername={auth.handleSaveUsername}
        handleBuyFreeze={auth.handleBuyFreeze} canBuyFreeze={auth.canBuyFreeze}
        handleCancelSubscription={auth.handleCancelSubscription}
      />;
    }

    return null;
  };

  return (
    <OnboardingProvider
      key={auth.user?.id || "guest"}
      user={auth.user}
      setUser={auth.setUser}
      setStep={setStep}
      refreshUser={auth.refreshUser}
    >
      {renderStep()}
    </OnboardingProvider>
  );
}

export default App;